import { useEffect, useMemo, useRef, useState } from "react";
import { generatePath, useLocation, useOutletContext, useParams } from "react-router";
import { styled } from "@pigment-css/react";
import type { MeetingPrepMessagePart } from "@acto/genui-components-schema";
import { SettingsGearOutline } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import {
  useCustomTalkingPoints,
  useDeleteCustomTalkingPoint,
} from "@acto/taskpane/core/data-access/custom-talking-points";
import { useRemovedMeetingPrepStories } from "@acto/taskpane/core/data-access/meeting-prep-story-removals";
import {
  useHiddenMeetingPrepCards,
  useRevealedMeetingPrepCards,
} from "@acto/taskpane/core/data-access/meeting-prep-card-hides";
import { useMeetingPrepLearnedDefaults } from "@acto/taskpane/core/data-access/meeting-prep-learned-defaults";
import { MeetingPrepProductExclusionsProvider } from "@acto/taskpane/field-sales-2/components/patterns/genui-meeting-prep/MeetingPrepProductExclusionsContext";
import { ProductExclusionConfirmation } from "@acto/taskpane/field-sales-2/components/patterns/genui-meeting-prep/ProductExclusionConfirmation";
import type { MeetingPrepCardHide, MeetingPrepCardReveal, MeetingPrepLearnedDefault } from "@acto/taskpane/core/domain";
import { useStructuredMeetingPrep } from "@acto/taskpane/field-sales-2/data-access/meeting-prep/useStructuredMeetingPrep";
import { AnalyticsEvents, useTrack, useViewTrackedOnce } from "@acto/taskpane/core/analytics";
import {
  isTodoStory,
  MeetingPrepTodosProvider,
  RemovableStoryCard,
  storyHasRenderableContent,
  TextPart,
  TodoStoryFrame,
  useMeetingPrepTodos,
} from "@acto/taskpane/field-sales-2/components/patterns/genui-meeting-prep";
import {
  CustomTalkingPointCard,
  CustomTalkingPointInput,
} from "@acto/taskpane/field-sales-2/components/patterns/custom-talking-points";
import { ShellSlotContent } from "@acto/taskpane/field-sales-2/shell";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { useDirectionalNavigate } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { PageLayout } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import type { CustomerOutletContext } from "@acto/taskpane/field-sales-2/CustomerDetailsLayout";
import { storyPartsToStories } from "@acto/taskpane/core/chat-envelope";
import { useContributeCustomerDetailScope } from "@acto/taskpane/field-sales-2/utils/useContributeCustomerDetailScope";

const ErrorLine = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  color: theme.fieldSalesTwo.colors.status.error.main,
}));

const Parts = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
}));

interface PartViewProps {
  part: MeetingPrepMessagePart;
  clientId: string;
  prepSessionId: string | null;
  cardHides?: MeetingPrepCardHide[];
  cardReveals?: MeetingPrepCardReveal[];
  learnedDefaults?: MeetingPrepLearnedDefault[];
  onStoryRemoved: (storyKey: string) => void;
  onStoryRestored: (storyKey: string) => void;
  onStoryDismissed: (storyKey: string) => void;
}

function PartView({
  part,
  clientId,
  prepSessionId,
  cardHides,
  cardReveals,
  learnedDefaults,
  onStoryRemoved,
  onStoryRestored,
  onStoryDismissed,
}: PartViewProps) {
  switch (part.type) {
    case "text":
      return <TextPart part={part} />;
    case "storyCard": {
      const storyCard = (
        <RemovableStoryCard
          card={part}
          clientId={clientId}
          prepSessionId={prepSessionId}
          cardHides={cardHides}
          cardReveals={cardReveals}
          learnedDefaults={learnedDefaults}
          onRemoved={onStoryRemoved}
          onRestored={onStoryRestored}
          onDismissed={onStoryDismissed}
        />
      );
      return isTodoStory(part) ? <TodoStoryFrame part={part}>{storyCard}</TodoStoryFrame> : storyCard;
    }
    default:
      part satisfies never;
      return null;
  }
}

export function MeetingPrep() {
  const { id: clientId = "" } = useParams<{ id: string }>();
  const { customer, isPrepGenerating } = useOutletContext<CustomerOutletContext>();
  const externalReference = customer?.externalReference;
  const {
    parts,
    sessionId: prepSessionId,
    generatedAt: prepGeneratedAt,
    error,
  } = useStructuredMeetingPrep(externalReference);
  const todosState = useMeetingPrepTodos(parts);
  const track = useTrack();
  useViewTrackedOnce(() => {
    if (!externalReference) return;
    track(AnalyticsEvents.MEETING_PREP_OPENED, {
      customerId: clientId,
      customerExternalReference: externalReference,
      customerName: customer?.name || "",
    });
  }, externalReference || null);
  const { data: customTalkingPoints } = useCustomTalkingPoints({ customerId: clientId, completed: false });
  const { deleteCustomTalkingPoint } = useDeleteCustomTalkingPoint();
  const { data: storyRemovals } = useRemovedMeetingPrepStories(clientId);
  const { data: cardHides } = useHiddenMeetingPrepCards(clientId, prepSessionId);
  const { data: cardReveals } = useRevealedMeetingPrepCards(clientId, prepSessionId);
  const { data: learnedDefaults } = useMeetingPrepLearnedDefaults();
  const prepLearnedDefaults = learnedDefaults?.filter((learnedDefault) =>
    learnedDefault.appliesToPrepGeneratedAt(prepGeneratedAt),
  );
  const tProfile = useTranslations("fieldSalesTwo.meetingPrepProfile");
  const navigate = useDirectionalNavigate();
  const location = useLocation();

  // Keys removed in this session stay rendered as the undo notice instead of being masked away.
  const [sessionRemovedKeys, setSessionRemovedKeys] = useState<ReadonlySet<string>>(new Set());
  const activeRemovalKeys = useMemo(() => new Set(storyRemovals?.map((removal) => removal.storyKey)), [storyRemovals]);

  const handleStoryRemoved = (storyKey: string) => {
    setSessionRemovedKeys((previous) => new Set(previous).add(storyKey));
  };
  const clearSessionRemoval = (storyKey: string) => {
    setSessionRemovedKeys((previous) => {
      const next = new Set(previous);
      next.delete(storyKey);
      return next;
    });
  };

  const visibleParts = useMemo(
    () =>
      parts.filter((part) => {
        if (!storyHasRenderableContent(part, todosState)) return false;
        if (part.type !== "storyCard" || !part.storyKey) return true;
        return !activeRemovalKeys.has(part.storyKey) || sessionRemovedKeys.has(part.storyKey);
      }),
    [parts, todosState, activeRemovalKeys, sessionRemovedKeys],
  );

  // Ambient context: the visible story cards; the agents resolve the full story via story_key.
  const storiesFragment = storyPartsToStories(visibleParts);
  useContributeCustomerDetailScope("prepare", storiesFragment);

  const talkingPointCount = customTalkingPoints?.length;
  const previousCountRef = useRef<number | null>(null);

  // Scroll to the end when a talking point is added (optimistic insert included) so the new
  // card lands in view right above the input. Scrolling to the document bottom keeps the card
  // clear of the fixed bottom nav (the layout reserves its height as content padding).
  // Initial load must not scroll.
  useEffect(() => {
    if (talkingPointCount === undefined) return;

    const previousCount = previousCountRef.current;
    previousCountRef.current = talkingPointCount;

    if (previousCount !== null && talkingPointCount > previousCount) {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
    }
  }, [talkingPointCount]);

  // The generating state (loader pill, hidden nav) is owned by the shell header
  // (CustomerDetailsLayout). While it's active this page keeps its body empty — otherwise a
  // returning customer's already-loaded talking points would show under the clean loader screen.
  return (
    <PageLayout hPadding="card">
      <MeetingPrepTodosProvider value={todosState}>
        <MeetingPrepProductExclusionsProvider clientId={clientId}>
          <ShellSlotContent slot="topBarTrailing" id="meetingPrepProfile">
            {!isPrepGenerating ? (
              <HeaderIconButton
                type="button"
                aria-label={tProfile("open")}
                onClick={() =>
                  navigate(generatePath(ActoRoutes.MEETING_PREP_PROFILE, { id: clientId }), {
                    direction: "push",
                    state: { from: location.pathname },
                  })
                }
              >
                <SettingsGearOutline aria-hidden="true" />
              </HeaderIconButton>
            ) : null}
          </ShellSlotContent>
          <ShellSlotContent slot="bottomNavAction" id="meetingPrepTalkingPoint">
            {!isPrepGenerating && clientId ? (
              <ProductExclusionConfirmation>
                <CustomTalkingPointInput clientId={clientId} />
              </ProductExclusionConfirmation>
            ) : null}
          </ShellSlotContent>
          {error && <ErrorLine>{error.message}</ErrorLine>}
          {!isPrepGenerating && (visibleParts.length > 0 || (customTalkingPoints?.length ?? 0) > 0) && (
            <Parts>
              {visibleParts.map((part, index) => (
                <PartView
                  key={part.type === "storyCard" && part.storyKey ? part.storyKey : `${part.type}-${index}`}
                  part={part}
                  clientId={clientId}
                  prepSessionId={prepSessionId}
                  cardHides={cardHides ?? []}
                  cardReveals={cardReveals}
                  learnedDefaults={prepLearnedDefaults}
                  onStoryRemoved={handleStoryRemoved}
                  onStoryRestored={clearSessionRemoval}
                  onStoryDismissed={clearSessionRemoval}
                />
              ))}
              {customTalkingPoints?.map((talkingPoint) => (
                <CustomTalkingPointCard
                  key={talkingPoint.id}
                  talkingPoint={talkingPoint}
                  onDelete={() => void deleteCustomTalkingPoint(talkingPoint.id)}
                />
              ))}
            </Parts>
          )}
        </MeetingPrepProductExclusionsProvider>
      </MeetingPrepTodosProvider>
    </PageLayout>
  );
}
